/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import cn from 'classnames';

import { useMediaQuery } from 'hooks/useMediaQuery';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './CapabilityShowcase.module.scss';

import type { CapabilityShowcaseProps, Panel } from './CapabilityShowcase.types';

const VH_PER_TAB = 90;
const BUFFER = 0.1;

const Tick = () => (
  <span className={styles.tick}>
    <svg viewBox="0 0 12 12" fill="none">
      <path d="M2 6.4 4.8 9.2 10 3.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const PanelFooter = ({ label, value, note }: Panel['foot']) => (
  <div className={styles.foot}>
    <div className={styles.score}>
      <span className={styles.scoreKey}>{label}</span>
      <b className={styles.scoreVal}>{value}</b>
    </div>
    <div className={styles.merge}>
      <Tick />
      {note}
    </div>
  </div>
);

const PanelView = ({ panel }: { panel: Panel }) => {
  if (panel.kind === 'diff') {
    return (
      <div className={styles.card}>
        <table className={styles.rt}>
          <thead>
            <tr>
              <th />
              <th>{panel.head[1]}</th>
              <th className={styles.src}>{panel.head[2]}</th>
            </tr>
          </thead>
          <tbody>
            {panel.rows.map((row) => (
              <tr key={row.label}>
                <td className={styles.rowKey}>{row.label}</td>
                <td className={cn(styles.old, row.oldMissing && styles.miss)}>{row.old}</td>
                <td className={styles.new}>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PanelFooter {...panel.foot} />
      </div>
    );
  }

  if (panel.kind === 'enrich') {
    return (
      <div className={styles.card}>
        <table className={styles.rt}>
          <thead>
            <tr>
              <th>{panel.head[0]}</th>
              <th className={styles.src}>{panel.head[1]}</th>
              <th>{panel.head[2]}</th>
            </tr>
          </thead>
          <tbody>
            {panel.rows.map((row) => (
              <tr key={row.label}>
                <td className={styles.rowKey}>{row.label}</td>
                <td className={styles.new}>{row.value}</td>
                <td className={styles.cite}>{row.cite}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PanelFooter {...panel.foot} />
      </div>
    );
  }

  if (panel.kind === 'financials') {
    return (
      <div className={styles.card}>
        <div className={styles.fvHead}>
          <div className={styles.fvId}>
            <span className={styles.fvAvatar}>{panel.entity.slice(0, 2).toUpperCase()}</span>
            <span>
              <span className={styles.fvName}>
                {panel.entity} <em className={styles.fvActive}>{panel.status}</em>
              </span>
              <span className={styles.fvSub}>{panel.entitySub}</span>
            </span>
          </div>
          <span className={styles.fvSrc}>{panel.source}</span>
        </div>
        <div className={styles.fvScroll}>
          <table className={cn(styles.rt, styles.rtFin)}>
            <thead>
              <tr>
                {panel.cols.map((col, i) => (
                  <th key={col} className={i === 0 ? undefined : styles.yr}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {panel.rows.map((row) => (
                <tr key={row.label}>
                  <td className={styles.rowKey}>{row.label}</td>
                  {row.values.map((value, i) => (
                    <td key={i} className={styles.fig}>
                      {value}
                    </td>
                  ))}
                  <td className={styles.trend}>
                    <span className={styles.bars}>
                      {row.bars.map((h, i) => (
                        <i key={i} style={{ height: `${h}px` }} />
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PanelFooter {...panel.foot} />
      </div>
    );
  }

  if (panel.kind === 'hierarchy') {
    return (
      <div className={styles.card}>
        <table className={styles.rt}>
          <thead>
            <tr>
              <th>{panel.head[0]}</th>
              <th>{panel.head[1]}</th>
              <th className={styles.src}>{panel.head[2]}</th>
            </tr>
          </thead>
          <tbody>
            {panel.rows.map((row) => (
              <tr key={row.name} className={row.root ? styles.gpRoot : undefined}>
                <td className={styles.rowKey}>
                  {!row.root && <span className={styles.tree}>└</span>}
                  {row.name}
                </td>
                <td className={styles.juris}>
                  <Image
                    className={styles.jflag}
                    src={`/storage/flags/${row.cc.toLowerCase()}.svg`}
                    alt=""
                    width={20}
                    height={14}
                    loading="lazy"
                    unoptimized
                  />
                  {row.juris}
                </td>
                <td className={styles.own}>{row.own}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PanelFooter {...panel.foot} />
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <table className={styles.rt}>
        <thead>
          <tr>
            <th>{panel.head[0]}</th>
            <th>{panel.head[1]}</th>
            <th className={styles.src}>{panel.head[2]}</th>
          </tr>
        </thead>
        <tbody>
          {panel.rows.map((row) => (
            <tr key={`${row.date}-${row.entity}`}>
              <td className={styles.rowKey}>{row.date}</td>
              <td>{row.entity}</td>
              <td className={styles.change}>
                <span className={styles.was}>{row.was}</span>
                <span className={styles.arr}>→</span>
                <b className={row.bad ? styles.bad : undefined}>{row.now}</b>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PanelFooter {...panel.foot} />
    </div>
  );
};

export const CapabilityShowcase = ({ title, description, tabs, className }: CapabilityShowcaseProps) => {
  const isDesktop = useMediaQuery(768);
  const trackRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState(0);

  const scrollToTab = (index: number) => {
    if (!trackRef.current || !isDesktop) {
      setActive(index);
      return;
    }

    const rect = trackRef.current.getBoundingClientRect();
    const trackTop = rect.top + window.scrollY;
    const viewportHeight = window.innerHeight;

    const progressPerTab = (1 - 2 * BUFFER) / tabs.length;
    const targetProgress = BUFFER + index * progressPerTab + progressPerTab / 2;
    const targetScrollY = trackTop - viewportHeight + targetProgress * (rect.height + viewportHeight);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: targetScrollY, behavior: reduced ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    if (!isDesktop) {
      return undefined;
    }

    const handleScroll = () => {
      if (!trackRef.current) {
        return;
      }

      const rect = trackRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const scrollProgress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (rect.height + viewportHeight)));

      let currentIndex: number;

      if (scrollProgress <= BUFFER) {
        currentIndex = 0;
      } else if (scrollProgress >= 1 - BUFFER) {
        currentIndex = tabs.length - 1;
      } else {
        const adjustedProgress = (scrollProgress - BUFFER) / (1 - 2 * BUFFER);
        currentIndex = Math.min(tabs.length - 1, Math.floor(adjustedProgress * tabs.length));
      }

      setActive(currentIndex);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop, tabs.length]);

  return (
    <section className={cn(styles.section, className)}>
      <div className={styles.container}>
        <div
          ref={trackRef}
          className={styles.track}
          style={isDesktop ? { height: `${tabs.length * VH_PER_TAB}vh` } : undefined}
        >
          <div className={styles.sticky}>
            <div className={styles.head}>
              <TitleBlock title={title} description={description} theme="dark" align="left" />
            </div>

            <div className={styles.body}>
              <div className={styles.tabs}>
                {tabs.map((tab, idx) => (
                  <button
                    key={tab.title}
                    type="button"
                    className={cn(styles.tab, idx === active && styles.tabActive)}
                    onClick={() => scrollToTab(idx)}
                  >
                    <h3>{tab.title}</h3>
                    <p>{tab.blurb}</p>
                  </button>
                ))}
              </div>

              <div className={styles.panel}>
                <PanelView panel={tabs[active].panel} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
